import { map } from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import { MouseEvent, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { initialize } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { FinancialDocumentPdfTemplateType } from '@mews-commander/data-access';
import { Device } from '@mews-commander/legacy-data-structures';
import { fetchPrinters } from '@mews-commander/legacy-entities/src/actions/printers';
import { getLanguages } from '@mews-commander/legacy-entities/src/ducks/languages';
import { getPrinters, isFetching } from '@mews-commander/legacy-entities/src/reducers/printers';
import { getCurrentEnterpriseDefaultLanguageCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { ApplicationState, CommanderThunkAction, CommanderThunkDispatch } from '@mews-commander/types';
import { Identifiable } from '@mews/std';
import { ExpandIcon, Icon, IconName, InputValue, ListDivider, ListItem, Menu } from '@optimus-web/core';
import { openDialog } from '../../../../../modules/dialog/reducer';
import { print, PrintFunctionParams, showPdf } from '../../../Actions/print';
import PdfLink from '../PdfLink';
import PrinterLink from '../PrinterLink';
import { formName, FormValues as PrintDialogFormValues } from '../PrintReasonDialogForm';
import AsyncDropdown from './AsyncDropdown';
import { HeadingPrintAction, PrintAction as PrintActionType } from './types';

type PrintFunction = (params: PrintFunctionParams, pdfUrl: string, reloadOnPrint?: boolean) => void;

interface PrintActionLanguage extends Identifiable {
    name: string;
}

interface DispatchProps {
    fetchEntities: () => void;
    onPdfClick: PrintFunction;
    onPrintClick: PrintFunction;
    openPrintReasonDialog: (values: PrintDialogFormValues) => void;
}

interface PrintActionProps extends DispatchProps {
    action: PrintActionType;
    entities: Array<typeof Device>;
    close: () => void;
    showPrintReasonDialog: boolean;
    setShowPrintReasonDialog: (show: boolean) => void;
    defaultLanguageCode: string;
    languages: Array<PrintActionLanguage>;
}

interface GetPrintParamsProps {
    printerId?: string;
    action: HeadingPrintAction;
    languageCode?: string;
    templateType?: FinancialDocumentPdfTemplateType | '';
}

export const getPrintParams = ({
    printerId = '',
    action,
    languageCode = '',
    templateType = '',
}: GetPrintParamsProps) => {
    const { pageSize, pageOrientation, footerLeftText, footerCenterText } = action;

    return {
        printPageSize: pageSize,
        printPageOrientation: pageOrientation,
        footerLeftText: footerLeftText || '',
        footerCenterText: footerCenterText || '',
        languageCode,
        printerId,
        templateType,
    };
};

const PrintAction = ({
    action,
    entities,
    close,
    onPdfClick,
    onPrintClick,
    openPrintReasonDialog,
    showPrintReasonDialog,
    setShowPrintReasonDialog,
    defaultLanguageCode,
    languages,
}: PrintActionProps) => {
    const [languageCode, setLanguageCode] = useState(defaultLanguageCode);
    const [languageMenuOpened, setLanguageMenuOpened] = useState(false);
    const languageMenuItem = useRef<HTMLElement>();

    const handlePrintFunctionClick = (callback: PrintFunction, printerId?: string) => {
        const params = getPrintParams({ printerId, action, languageCode });
        const { requirePrintReasonOnReprint, isReprint, reloadOnPrint, url: pdfUrl } = action;

        if (showPrintReasonDialog || isReprint) {
            openPrintReasonDialog({
                printParams: params,
                type: 'pdf',
                pdfUrl,
                reloadOnPrint,
            });
        } else {
            callback(params, pdfUrl, reloadOnPrint);

            if (requirePrintReasonOnReprint) setShowPrintReasonDialog(true);
        }
        close();
    };

    const handlePdfLinkClick = () => handlePrintFunctionClick(onPdfClick);

    const handlePrinterSelected = (printerId: string) => handlePrintFunctionClick(onPrintClick, printerId);

    const handleLanguageSelected = (e: MouseEvent, code?: InputValue) => {
        e.nativeEvent.stopImmediatePropagation();

        if (code) {
            setLanguageCode(code as string);
        }
        setLanguageMenuOpened(false);
    };

    const handleLanguageMenuItemClick = (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();

        setLanguageMenuOpened(true);
    };

    const menuItemRefHandler = (c: HTMLElement) => {
        languageMenuItem.current = c;
    };

    const physicalPrinters = map((printer) => <PrinterLink printer={printer} onClick={handlePrinterSelected} />)(
        entities
    );

    const renderLanguageMenuItems = map(({ id, name }: PrintActionLanguage) => (
        <ListItem key={id} value={id} primaryText={name} onClick={handleLanguageSelected} />
    ));

    let languageSelectMenu;
    if (action.withLanguageSelect) {
        const language = languages.find(({ id }) => id === languageCode);

        languageSelectMenu = (
            <>
                <ListItem
                    primaryText={language?.name}
                    onClick={handleLanguageMenuItemClick}
                    innerRef={menuItemRefHandler}
                    leftIcon={<Icon value={IconName.Languages} />}
                    rightIcon={<ExpandIcon expanded={languageMenuOpened} />}
                />
                <Menu opened={languageMenuOpened} anchorElement={languageMenuItem.current}>
                    {renderLanguageMenuItems(languages)}
                </Menu>
                <ListDivider />
            </>
        );
    }

    return (
        <div>
            {languageSelectMenu}
            <PdfLink onClick={handlePdfLinkClick} />
            <ListDivider />
            {physicalPrinters}
        </div>
    );
};

const selector = createStructuredSelector({
    entities: getPrinters,
    isFetching,
    languages: getLanguages,
    defaultLanguageCode: getCurrentEnterpriseDefaultLanguageCode,
});

const fetchEntities = (): CommanderThunkAction => (dispatch) => {
    const enterpriseId = getCurrentEnterpriseId();
    dispatch(fetchPrinters({ enterpriseId }));
};

export default pipe(
    PrintAction,
    AsyncDropdown({ icon: IconName.Print }),
    connect<{}, DispatchProps, {}, ApplicationState>(selector, (dispatch: CommanderThunkDispatch) => ({
        fetchEntities: () => dispatch(fetchEntities()),
        onPdfClick: (params, pdfUrl, reloadOnPrint) => dispatch(showPdf(params, pdfUrl, reloadOnPrint)),
        onPrintClick: (params, pdfUrl, reloadOnPrint) => dispatch(print(params, pdfUrl, reloadOnPrint)),
        openPrintReasonDialog: (values) => {
            dispatch(openDialog(formName));
            dispatch(initialize(formName, values));
        },
    }))
);
